/**
 * @module features/clients/components/ClientFilters
 * @description Search and status filter toolbar matching the system aesthetic of UserFilters & DeviceFilterBar.
 * Features a rounded-2xl container, ⌘K search indicator, clear button, and segmented pill buttons.
 */
import * as React from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";

export interface ClientFiltersProps {
  search: string;
  status: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (status: string) => void;
}

export function ClientFilters({
  search,
  status,
  onSearchChange,
  onStatusChange,
}: ClientFiltersProps) {
  const [localSearch, setLocalSearch] = React.useState(search);

  // Debounce search update by 250ms
  React.useEffect(() => {
    if (localSearch === search) return;
    const handler = setTimeout(() => {
      onSearchChange(localSearch);
    }, 250);

    return () => clearTimeout(handler);
  }, [localSearch, search, onSearchChange]);

  // Sync external search changes
  React.useEffect(() => {
    setLocalSearch((prev) => (prev !== search ? search : prev));
  }, [search]);

  const statuses = [
    { label: "All Clients", value: "all" },
    { label: "Active", value: "active" },
    { label: "Suspended", value: "suspended" },
    { label: "Inactive", value: "inactive" },
  ];

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-card dark:bg-zinc-950 p-3 sm:p-3.5 rounded-2xl border border-border/70 dark:border-zinc-800/80 shadow-xs">
      {/* Search Input with ⌘K Badge */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground shrink-0 pointer-events-none" />
        <Input
          type="text"
          placeholder="Search by organization name, email, or city..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="pl-10 pr-12 h-10 text-sm bg-muted/40 dark:bg-zinc-900 border-border/70 dark:border-zinc-800 rounded-xl focus-visible:ring-1 focus-visible:ring-ring"
        />
        {localSearch ? (
          <button
            type="button"
            onClick={() => {
              setLocalSearch("");
              onSearchChange("");
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center pointer-events-none">
            <span className="text-xs text-muted-foreground/70 font-mono">⌘K</span>
          </div>
        )}
      </div>

      {/* Segmented Filter Pills */}
      <div className="flex items-center gap-1 p-1 bg-muted/60 dark:bg-zinc-900 rounded-xl border border-border/40 dark:border-zinc-800/80 overflow-x-auto">
        {statuses.map((s) => {
          const isActive = status === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => onStatusChange(s.value)}
              className={`flex-1 sm:flex-none px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all duration-150 whitespace-nowrap min-h-[36px] sm:min-h-0 ${
                isActive
                  ? "bg-card dark:bg-zinc-800 text-foreground shadow-xs border border-border/40 dark:border-zinc-700"
                  : "text-muted-foreground hover:text-foreground hover:bg-card/40"
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
